import { TagSchema } from "@redotech/redo-common-model-tags-common/tag";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { trackerStatusSchema } from "./order-tracking-tracker-status-definition";
import {
  trackingEventInputSchema,
  trackingEventSchema,
} from "./order-tracking-tracking-event-definition";
import { OrderTrackingShipmentStatus } from "./order-tracking-types";

const milestoneDate = z.coerce.date().nullish();

export const milestoneColumnsSchema = {
  preTransitAt: milestoneDate,
  inTransitAt: milestoneDate,
  outForDeliveryAt: milestoneDate,
  deliveredAt: milestoneDate,
  availableForPickupAt: milestoneDate,
  availableForPickupCarrierAt: milestoneDate,
  stalledInTransitAt: milestoneDate,
  returnToSenderAt: milestoneDate,
  deliveryAttemptedAt: milestoneDate,
  deliveryFailureAt: milestoneDate,
  shipmentCancelledAt: milestoneDate,
  shipmentErrorAt: milestoneDate,
};

export const MILESTONE_KEYS = [
  "preTransitAt",
  "inTransitAt",
  "outForDeliveryAt",
  "deliveredAt",
  "availableForPickupAt",
  "availableForPickupCarrierAt",
  "stalledInTransitAt",
  "returnToSenderAt",
  "deliveryAttemptedAt",
  "deliveryFailureAt",
  "shipmentCancelledAt",
  "shipmentErrorAt",
] as const;

export type MilestoneKey = (typeof MILESTONE_KEYS)[number];

const orderTrackingShipmentInputSchema_ = z.object({
  trackingCode: z.string().nullish(),
  trackingUrl: z.string().nullish(),
  trackingProvider: z.string().nullish(),
  trackingProviderId: z.string().nullish(),
  carrier: z.string().nullish(),
  carrierService: z.string().nullish(),
  unmappedCarrier: z.string().nullish(),
  status: trackerStatusSchema,
  estimatedDeliveryAt: z.coerce.date().nullish(),
  ...milestoneColumnsSchema,
  trackingEvents: z.array(trackingEventInputSchema).nullish(),
});
export const orderTrackingShipmentInputSchema: z.ZodType<OrderTrackingShipmentInput> =
  orderTrackingShipmentInputSchema_;
export interface OrderTrackingShipmentInput extends z.infer<
  typeof orderTrackingShipmentInputSchema_
> {}

const orderTrackingShipmentSchema_ = z.object({
  id: z.coerce.bigint(),
  accountId: z.coerce.bigint(),
  trackingCode: z.string().nullish(),
  trackingUrl: z.string().nullish(),
  trackingProvider: z.string().nullish(),
  trackingProviderId: z.string().nullish(),
  carrier: z.string().nullish(),
  carrierService: z.string().nullish(),
  unmappedCarrier: z.string().nullish(),
  status: createEnum(OrderTrackingShipmentStatus),
  estimatedDeliveryAt: z.coerce.date().nullish(),
  lastTrackingUpdateAt: z.coerce.date().nullish(),
  sourceCreatedAt: z.coerce.date().nullish(),
  ...milestoneColumnsSchema,
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export const orderTrackingShipmentSchema: z.ZodType<OrderTrackingShipment> =
  orderTrackingShipmentSchema_;
export interface OrderTrackingShipment extends z.infer<
  typeof orderTrackingShipmentSchema_
> {}

const orderTrackingShipmentWithTrackingEventsSchema_ =
  orderTrackingShipmentSchema_.extend({
    trackingEvents: z.array(trackingEventSchema),
  });
export const orderTrackingShipmentWithTrackingEventsSchema: z.ZodType<OrderTrackingShipmentWithTrackingEvents> =
  orderTrackingShipmentWithTrackingEventsSchema_;
export interface OrderTrackingShipmentWithTrackingEvents extends z.infer<
  typeof orderTrackingShipmentWithTrackingEventsSchema_
> {}

export interface InsertShipmentInput {
  accountId: bigint;
  trackingCode?: string | null;
  trackingUrl?: string | null;
  trackingProvider?: string | null;
  trackingProviderId?: string | null;
  carrier?: string | null;
  carrierService?: string | null;
  unmappedCarrier?: string | null;
  estimatedDeliveryAt?: Date | null;
  preTransitAt?: Date | null;
  inTransitAt?: Date | null;
  outForDeliveryAt?: Date | null;
  deliveredAt?: Date | null;
  availableForPickupAt?: Date | null;
  availableForPickupCarrierAt?: Date | null;
  stalledInTransitAt?: Date | null;
  returnToSenderAt?: Date | null;
  deliveryAttemptedAt?: Date | null;
  deliveryFailureAt?: Date | null;
  shipmentCancelledAt?: Date | null;
  shipmentErrorAt?: Date | null;
  sourceCreatedAt?: Date | null;
}

const orderTrackingShipmentSummarySchema_ = z.object({
  _id: z.string(),
  orderId: z.string().nullish(),
  orderName: z.string().nullish(),
  orderNames: z.array(z.string()).nullish(),
  externalOrderId: z.string().nullish(),
  customerName: z.string().nullish(),
  customerEmail: z.string().nullish(),
  orderDate: z.coerce.date().nullish(),
  status: z.string().nullish(),
  trackingCode: z.string().nullish(),
  carrier: z.string().nullish(),
  firstInTransitAt: z.coerce.date().nullish(),
  estimatedDeliveryAt: z.coerce.date().nullish(),
  outForDeliveryAt: z.coerce.date().nullish(),
  deliveredAt: z.coerce.date().nullish(),
  shippingCity: z.string().nullish(),
  shippingProvince: z.string().nullish(),
  shippingCountry: z.string().nullish(),
  shippingZip: z.string().nullish(),
  tags: z.array(TagSchema).nullish(),
  internalNote: z.string().nullish(),
  salesChannel: z.string().nullish(),
});
export const orderTrackingShipmentSummarySchema: z.ZodType<OrderTrackingShipmentSummary> =
  orderTrackingShipmentSummarySchema_;
export interface OrderTrackingShipmentSummary extends z.infer<
  typeof orderTrackingShipmentSummarySchema_
> {}

const orderTrackingShipmentMetricsSchema_ = z.object({
  totalShipments: z.number(),
  inTransit: z.number(),
  delivered: z.number(),
  exceptions: z.number(),
});
export const orderTrackingShipmentMetricsSchema: z.ZodType<OrderTrackingShipmentMetrics> =
  orderTrackingShipmentMetricsSchema_;
export interface OrderTrackingShipmentMetrics extends z.infer<
  typeof orderTrackingShipmentMetricsSchema_
> {}
